import {useCallback, useEffect, useReducer, useState} from "react";
import {useTemporalState} from "./FiltersPanel/TemporalFilters";
import {useCurrentUser} from "../../../auth/CurrentUserProvider";


const layersReducer = (state, action) => {
    const layer = action.layer;
    switch (action.type) {
        case "toggleLayer":
            return {
                ...state,
                [layer]: {
                    ...state[layer],
                    active: !state[layer].active
                }
            };

        case "updateLayer":
            return {
                ...state,
                [layer]: {
                    ...state[layer],
                    ...action.updates
                }
            }

        case "updateLayerFilters":
            return {
                ...state,
                [layer]: {
                    ...state[layer],
                    filters: {
                        ...state[layer].filters,
                        ...action.filters
                    }
                }
            };

        case "resetLayerFilters":
            return {
                ...state,
                [layer]: {
                    ...state[layer],
                    filters: {}
                }
            };

        case "updateCommonFilter":
            return {
                ...state,
                commonFilters: {
                    ...state.commonFilters,
                    [action.filterName]: action.filterValue
                }
            };

        default:
            return state;
    }
}

const useLayersState = () => {
    const temporalState = useTemporalState();
    const {currentUser} = useCurrentUser();
    const initialSelectedWorkgroups = currentUser.data.role === "ADMIN" ? [] : currentUser.data.workgroups;

    const [selectedWorkgroups, setSelectedWorkgroups] = useState(initialSelectedWorkgroups);

    const initialLayerState = {
        model1Stations: {
            active: true,
            filters: {
                stationType: null,
                status: null,
                validationStatus: null,
                user: [],
                stationAclModel1Write: false,
                microZones: [],
            },
            visibleParam: "hs"
        },
        models23: {
            active: false,
            filters: {
                stationType: null,
                status: null,
                user: [],
                stationAclModel23Write: false,
                microZones: [],
            }
        },
        stations: {
            active: false,
            filters: {
                visibility: null,
                province: null,
                municipality: null,
                type: null,
                microZones: [],
                currentlyActive: 'true',
                acceptsModel1: '',
                acceptsModel23: '',
            }
        },
        microzones: {
            active: false,
            filters: {
                microZones: [],
            },
            visibleParam: "l1_max"
        },
        incidents: {
            active: false,
            filters: {
                stationType: null,
                status: null,
                validationStatus: null,
                user: [],
                stationAclModel1Write: false,
                incidentCategory: null,
                incidentSubcategory: null,
                region: null,
                province: null,
                municipality: null,
                microZones: [],
            },
            visibleParam: "incident_severity"
        },
        commonFilters: {
            search: "",
            workgroupIds: selectedWorkgroups.map(w => w.id).join(','),
            dateGte: temporalState.filterDateGte,
            dateLte: temporalState.filterDateLte,
            elevationGte: null,
            elevationLte: null,
            aspects: [],
        }
    }

    const [layers, layersDispatcher] = useReducer(layersReducer, initialLayerState);

    const toggleLayer = useCallback((layer) => {
        layersDispatcher({type: "toggleLayer", layer});
    }, []);

    const updateLayerFilters = useCallback((layer, filters) => {
        layersDispatcher({
            type: "updateLayerFilters",
            layer,
            filters
        });
    }, []);

    const updateCommonFilter = useCallback((filterName, filterValue) => {
        layersDispatcher({
            type: "updateCommonFilter",
            filterName,
            filterValue
        });
    }, []);

    const setVisibleParam = useCallback((layer, param) => {
        layersDispatcher({
            type: "updateLayer",
            layer,
            updates: {
                visibleParam: param
            }
        });
    }, []);

    useEffect(() => {
        if (layers.commonFilters.dateGte?.getTime() !== temporalState.filterDateGte?.getTime()) {
            updateCommonFilter("dateGte", temporalState.filterDateGte);
        }
    }, [temporalState.filterDateGte, layers.commonFilters.dateGte, updateCommonFilter]);

    useEffect(() => {
        if (layers.commonFilters.dateLte?.getTime() !== temporalState.filterDateLte?.getTime()) {
            updateCommonFilter("dateLte", temporalState.filterDateLte);
        }
    }, [temporalState.filterDateLte, layers.commonFilters.dateLte, updateCommonFilter]);

    return {
        layers,
        toggleLayer,
        updateLayerFilters,
        updateCommonFilter,
        setVisibleParam,
        temporalState,
        selectedWorkgroups,
        setSelectedWorkgroups
    }
}

export default useLayersState;